import EditFieldModal from "@/modules/common/components/modals/edit-field-modal";
import { Button, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { usePublishListing } from "@/modules/property-tracks/hooks/listings/publish-listing.hook";
import { showToast } from "@/modules/common/utils";
import { TOAST_IDS, ToastType } from "@/modules/common/types";
import MissingDataModal from "../common/missing-data-modal";
import { ResaleListingFormFields } from "@/modules/property-tracks/utils";
import EditResaleListing from "./forms/edit";
import useResaleListingForms from "@/modules/property-tracks/hooks/listings/resale-listing/resale-forms.hook";
import { useReadonlyContext } from "@/modules/common/context/read-only.context";

export default function PublishResaleListing({
  listingUid,
  trackUid,
  values,
}: {
  values: ResaleListingFormFields;
  trackUid: string;
  listingUid: string;
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [missingDataModalOpen, setMissingDataModalOpen] = useState(false);
  const [firstErrorStep, setFirstErrorStep] = useState<number | null>(null);

  const { validate, reset } = useResaleListingForms({ defaultValues: values });

  useEffect(() => reset(values), [values]);

  const mutation = usePublishListing();

  const handleConfirm = async () => {
    const { isValid, firstErrorStep } = await validate(true);

    if (isValid) {
      mutation.mutate(listingUid, {
        onSuccess: () => {
          handleModalClose();
          showToast({
            type: ToastType.SUCCESS,
            message: "Listing published successfully",
            toastId: TOAST_IDS.PUBLISH_LISTING_SUCCESS,
          });
        },
        onError: (error) => {
          showToast({
            type: ToastType.ERROR,
            message:
              error?.response?.data?.message ||
              "Unexpected error occurred. Please try again.",
            toastId: TOAST_IDS.PUBLISH_LISTING_ERROR,
          });
        },
      });
    } else {
      // Validation succeeded
      setFirstErrorStep(firstErrorStep);
      setMissingDataModalOpen(true);
      // handleModalClose();
    }
  };

  const handleModalOpen = () => {
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const { readonly } = useReadonlyContext();

  return (
    <>
      <Button
      disabled={readonly}
        variant="contained"
        onClick={handleModalOpen}
        sx={(theme) => ({
          display: "flex",
          gap: theme.nawy.spacing.xs,
          alignItems: "center",
          backgroundColor: theme.nawy.colors.semantic.layer.accent,
          textTransform: "none",
          ...theme.nawy.typography.english.variants.body.md.semibold,
        })}
      >
        Publish listing
      </Button>
      <EditFieldModal
        open={modalOpen}
        setOpen={setModalOpen}
        header="Publish listing"
        width={480}
        handleConfirm={handleConfirm}
        handleCloseModal={handleModalClose}
        isLoading={mutation.isPending}
        confirmText="Publish"
      >
        <Typography
          sx={(t) => t.nawy.typography.english.variants.body.md.regular}
        >
          Are you sure you want to publish this listing?
        </Typography>
      </EditFieldModal>
      <MissingDataModal
        open={missingDataModalOpen}
        handleOpen={() => setMissingDataModalOpen(true)}
        handleClose={() => {
          setMissingDataModalOpen(false);
        }}
        handleConfirm={() => {
          setEditModalOpen(true);
          setMissingDataModalOpen(false);
          setModalOpen(false);
        }}
      />
      <EditResaleListing
        open={editModalOpen}
        setOpen={setEditModalOpen}
        values={values}
        listingUid={listingUid}
        trackUid={trackUid}
        defaultStep={firstErrorStep ?? undefined}
      />
    </>
  );
}
